/**
 * Detections-panel sort logic (REQ-UI-110).
 *
 * The pure sort for the Detections list — the default order + the
 * selection-driven sort that resolves ``platform_name`` via the producer-name
 * join. Lives in this non-component module (not in
 * :file:`./detections-panel.tsx`) so the panel file exports only its component
 * (fast-refresh purity) and the sort is unit-testable in isolation — the same
 * split as :file:`./detection-display`.
 *
 * Cross-references:
 *   - REQ-UI-110 (operator list sort) / REQ-UI-051 (non-mutating; coexists with
 *     id-keyed selection).
 *   - REQ-INGEST-044 (nullable classification / confidence → nulls-last).
 *   - @/lib/list-sort (the engine + comparator kit);
 *     @/lib/use-producer-names (the join).
 */
import type { DetectionRecord } from "@/detections/detections-store";
import type { ProducerNameIndex } from "@/lib/use-producer-names";
import {
  type ComparatorMap,
  compareIso,
  compareNumbers,
  compareStringsCI,
  type SortSelection,
  sortBy,
} from "@/lib/list-sort";
import type { DetectionListSort } from "@/auth/session-types";

/**
 * The Detections panel's built-in default order: time descending (newest
 * first) — REQ-UI-110. Used when the working state carries no
 * ``list_sort.detections`` selection.
 */
export const DETECTION_DEFAULT_SORT: SortSelection<DetectionListSort["field"]> = {
  field: "time",
  direction: "desc",
};

/**
 * Sort the detection records by the operator's selection (REQ-UI-110),
 * resolving the ``platform_name`` field via the producer-name join.
 *
 *   - ``time`` — chronological (the default, ``desc`` = newest first).
 *   - ``classification`` — case-insensitive on ``primary_class``; a null
 *     classification routes to the tail (nulls-last), NOT the "Unclassified"
 *     label — an absent classification is unknown, not a name beginning with
 *     "U".
 *   - ``platform_name`` — case-insensitive on the joined display name (falls
 *     back to the raw ``producer_id`` for an unresolved producer, so the row is
 *     still ordered, never dropped).
 *   - ``confidence`` — numeric on ``detection_confidence``; null routes to the
 *     tail.
 *
 * Non-mutating (the store buffer is left untouched), so the sort never disturbs
 * the id-keyed selection or the keep-in-view anchor (REQ-UI-051).
 *
 * @param detections - The recent-detections buffer (newest-first as stored).
 * @param names - The ``producer_id → display_name`` resolver.
 * @param selection - The active sort selection (panel default when none).
 * @returns A new, sorted array.
 */
export function sortDetectionRows(
  detections: readonly DetectionRecord[],
  names: ProducerNameIndex,
  selection: SortSelection<DetectionListSort["field"]>,
): DetectionRecord[] {
  const comparators: ComparatorMap<DetectionRecord, DetectionListSort["field"]> = {
    time: (a, b) => compareIso(a.time, b.time),
    classification: (a, b) =>
      // Present-only here (nulls partitioned out below); ?? "" is a safe no-op.
      compareStringsCI(a.primary_class ?? "", b.primary_class ?? ""),
    platform_name: (a, b) =>
      compareStringsCI(names.nameFor(a.producer_id), names.nameFor(b.producer_id)),
    confidence: (a, b) => compareNumbers(a.detection_confidence ?? 0, b.detection_confidence ?? 0),
  };
  // classification + confidence are nullable (REQ-INGEST-044) → nulls-last for
  // whichever is the active field; time + platform_name are always present.
  const isAbsent =
    selection.field === "classification"
      ? (d: DetectionRecord): boolean => d.primary_class === null
      : selection.field === "confidence"
        ? (d: DetectionRecord): boolean => d.detection_confidence === null
        : undefined;
  return sortBy(detections, selection, comparators, isAbsent);
}
